import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { UserMessageData, UserMessageType } from "../models/user-message.model";
import { UserMessageRemote } from "../../infrastructure/user-message.remote";
import { UserMessageStorageService } from "./user-message-storage.service";
import { SsoAccountFacade } from "../../../sso-account/domain/+state/sso-account.facade";

@Injectable({
  providedIn: "root",
})
export class UserMessageFacade {
  constructor(
    private readonly remote: UserMessageRemote,
    private readonly storage: UserMessageStorageService,
    private readonly ssoAccountFacade: SsoAccountFacade,
  ) {}

  getNewPopupMessages(entityIds?: number[]): Observable<UserMessageData[]> {
    return this.remote.getMessages(entityIds, UserMessageType.PopUp).pipe(
      map(messages => this.storage.filterNewMessages(messages ?? []))
    );
  }

  getOnScreenMessages(entityIds?: number[]): Observable<UserMessageData[]> {
    return this.remote.getMessages(entityIds, UserMessageType.OnScreen);
  }

  dismissMessage(message: UserMessageData): Observable<void> {
    return this.callDismissApi(message, true);
  }

  undismissMessage(message: UserMessageData): Observable<void> {
    return this.callDismissApi(message, false);
  }

  private callDismissApi(message: UserMessageData, dismiss: boolean): Observable<void> {
    if (!message.dismissable) {
      return of(undefined);
    }
    if (message.isEntityMessage && !message.entityId) {
      return of(undefined);
    }

    return this.ssoAccountFacade.getCurrentSsoAccount$().pipe(
      take(1),
      switchMap(ssoAccount => {
        if (message.isEntityMessage) {
          return this.remote.dismissEntityMessage(message.id, message.entityId, dismiss);
        }
        return this.remote.dismissCustomerMessage(message.id, ssoAccount.id, dismiss);
      })
    );
  }

  markAsDisplayed(message: UserMessageData): void {
    this.storage.markMessageAsDisplayed(message);
  }

  getMessageFingerprint(message: UserMessageData): string {
    return this.storage.getMessageFingerprint(message);
  }
}
